import React, { useEffect, useState, useCallback, useRef } from 'react';
import * as signalR from '@microsoft/signalr';
import { C, Btn, Card, Badge, Alert, Input, Spinner } from '../../components/UI';
import { useAuth } from '../../context/AuthContext';
import { useBranding } from '../../context/BrandingContext';
import { api, VendorDocumentStatusResponse, VendorConnectStatusResponse, VendorPayoutHistoryItem } from '../../services/api';
import { useIdleDisconnect } from '../../hooks/useIdleDisconnect';

const API_BASE = process.env.REACT_APP_API_URL || 'http://10.0.0.175:5000';

// ── Vendor Portal ─────────────────────────────────────────────────────────────

type View = 'orders' | 'sizes' | 'earnings' | 'documents';

export default function VendorPortal() {
    const { user, logout } = useAuth();
    const branding = useBranding();
    const [view, setView] = useState<View>('orders');
    const [orders, setOrders] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [notification, setNotification] = useState('');
    const [docStatus, setDocStatus] = useState<VendorDocumentStatusResponse | null>(null);
    const [docStatusLoading, setDocStatusLoading] = useState(true);

    const fetchOrders = useCallback(async () => {
        try {
            const data = await api.getVendorOrders();
            setOrders(data);
        } catch (e: any) { setError(e.message); }
        finally { setLoading(false); }
    }, []);

    const fetchDocStatus = useCallback(async () => {
        try {
            const data = await api.getVendorDocumentStatus();
            setDocStatus(data);
        } catch { /* non-fatal */ }
        finally { setDocStatusLoading(false); }
    }, []);

    useEffect(() => {
        fetchOrders();
        fetchDocStatus();
    }, [fetchOrders, fetchDocStatus]);

    // SignalR — listen for new orders and status changes. Idle-gated (see useIdleDisconnect) so
    // a vendor dashboard left open on a screen doesn't keep an always-open socket going and block
    // the serverless DB from ever getting a clean overnight pause window.
    const hubRef = useRef<signalR.HubConnection | null>(null);
    const connectHub = useCallback(() => {
        const token = localStorage.getItem('br_token');
        if (!token) return;

        const hub = new signalR.HubConnectionBuilder()
            .withUrl(`${API_BASE}/hubs/tracking?access_token=${token}`, { withCredentials: false })
            .withAutomaticReconnect()
            .build();

        hub.on('DumpsterOrderUpdated', () => {
            fetchOrders();
        });

        hub.on('NewDumpsterOrder', (data: any) => {
            setNotification(`New order received at ${data.address || 'your service area'}!`);
            fetchOrders();
            setTimeout(() => setNotification(''), 5000);
        });

        hub.onreconnected(() => {
            hub.invoke('JoinVendorGroup', user?.userId ?? '').catch(() => {});
        });

        hubRef.current = hub;
        hub.start()
            .then(() => hub.invoke('JoinVendorGroup', user?.userId ?? '').catch(() => {}))
            .catch(console.error);
    }, [fetchOrders, user?.userId]);

    useEffect(() => {
        connectHub();
        return () => { hubRef.current?.stop(); hubRef.current = null; };
    }, [connectHub]);

    useIdleDisconnect({
        onIdle: () => { hubRef.current?.stop().catch(() => {}); },
        onActive: () => {
            if (hubRef.current?.state === signalR.HubConnectionState.Disconnected) connectHub();
        },
    });

    const isApproved = docStatus?.onboardingStatus === 'Approved';
    const isPending = !docStatus || docStatus.onboardingStatus === 'Incomplete';
    const isUnderReview = docStatus?.onboardingStatus === 'UnderReview';

    const statusColor = (s: string) => {
        const colors: Record<string, string> = {
            Pending: '#854F0B',
            VendorAccepted: '#3730A3',
            Delivered: C.greenMid,
            LoadingInProgress: '#6366F1',
            PickupRequested: '#854F0B',
            PickupScheduled: '#3730A3',
            Completed: C.green,
            Cancelled: C.textMuted,
        };
        return colors[s] || C.textMuted;
    };

    const navItems: { key: View; label: string }[] = [
        { key: 'orders', label: `📋 Orders${orders.filter(o => o.status === 'Pending').length > 0 ? ` (${orders.filter(o => o.status === 'Pending').length} new)` : ''}` },
        { key: 'sizes', label: '🗑️ My Sizes' },
        { key: 'earnings', label: '💰 Earnings' },
        { key: 'documents', label: `📄 Documents${!isApproved ? ' ●' : ''}` },
    ];

    return (
        <div style={{ minHeight: '100vh', background: C.bg, fontFamily: "'Inter', sans-serif" }}>
            {/* Header */}
            <div style={{ background: branding.primaryColor, padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 56 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    {branding.logoUrl
                        ? <img src={branding.logoUrl} alt={branding.appName} style={{ height: 32, maxWidth: 120, objectFit: 'contain' }} />
                        : <span style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontStyle: 'italic', fontSize: 16, letterSpacing: 1, color: branding.navTextColor ?? '#ffffff' }}>
                            {branding.appName}
                          </span>
                    }
                    <span style={{ color: branding.navTextColor ?? '#ffffff', fontSize: 13, opacity: 0.7 }}>· {user?.name || 'Vendor Portal'}</span>
                </div>
                <button onClick={logout} style={{ background: 'none', border: '1px solid var(--color-accent, #4ADE80)', color: 'var(--color-accent, #4ADE80)', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13 }}>Sign out</button>
            </div>

            {/* New order notification */}
            {notification && (
                <div style={{ background: C.greenMid, color: '#fff', padding: '10px 24px', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                    🔔 {notification}
                </div>
            )}

            {/* Onboarding status banner */}
            {docStatus && !isApproved && (
                <div style={{
                    background: isUnderReview ? '#EEF2FF' : '#FFF7E6',
                    borderBottom: `1px solid ${isUnderReview ? '#6366F1' : '#EF9F27'}`,
                    padding: '12px 24px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 10,
                }}>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 14, color: isUnderReview ? '#3730A3' : '#854F0B', marginBottom: 2 }}>
                            {isUnderReview ? '⏳ Documents Under Review' : '📋 Complete Your Onboarding'}
                        </div>
                        <div style={{ fontSize: 13, color: isUnderReview ? '#4338CA' : '#92400E' }}>
                            {isUnderReview
                                ? 'Your documents have been submitted and are being reviewed by an admin. You can manage orders once approved.'
                                : 'Upload your Business License, Liability Insurance, and Equipment proof to start accepting orders.'}
                        </div>
                    </div>
                    {!isUnderReview && (
                        <Btn size="sm" onClick={() => setView('documents')}>Upload documents →</Btn>
                    )}
                </div>
            )}

            {/* Nav */}
            <div style={{ background: '#fff', borderBottom: `1px solid ${C.border}`, padding: '0 24px', display: 'flex', gap: 4 }}>
                {navItems.map(n => (
                    <button key={n.key} onClick={() => setView(n.key)}
                        style={{ padding: '14px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: view === n.key ? 700 : 400, color: view === n.key ? C.greenMid : C.textSub, borderBottom: view === n.key ? `2px solid ${C.greenMid}` : '2px solid transparent' }}>
                        {n.label}
                    </button>
                ))}
            </div>

            <div style={{ maxWidth: 960, margin: '0 auto', padding: 24 }}>
                <Alert message={error} />

                {view === 'documents' && (
                    <DocumentsView docStatus={docStatus} onUpdated={ds => { setDocStatus(ds); fetchDocStatus(); }} />
                )}

                {view === 'orders' && (
                    docStatusLoading ? <Spinner /> :
                    isApproved
                        ? <OrdersView orders={orders} loading={loading} onRefresh={fetchOrders} statusColor={statusColor} />
                        : <OnboardingGate onboardingStatus={docStatus?.onboardingStatus ?? 'Incomplete'} onGoToDocs={() => setView('documents')} />
                )}

                {view === 'sizes' && (
                    docStatusLoading ? <Spinner /> :
                    isApproved
                        ? <SizesView />
                        : <OnboardingGate onboardingStatus={docStatus?.onboardingStatus ?? 'Incomplete'} onGoToDocs={() => setView('documents')} />
                )}

                {view === 'earnings' && (
                    <EarningsView orders={orders} />
                )}
            </div>
        </div>
    );
}

// ── Onboarding Gate ───────────────────────────────────────────────────────────

function OnboardingGate({ onboardingStatus, onGoToDocs }: { onboardingStatus: string; onGoToDocs: () => void }) {
    const isReview = onboardingStatus === 'UnderReview';
    return (
        <Card style={{ textAlign: 'center', padding: 56 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>{isReview ? '⏳' : '📋'}</div>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
                {isReview ? 'Documents Under Review' : 'Onboarding Required'}
            </div>
            <div style={{ fontSize: 14, color: C.textSub, marginBottom: 24, maxWidth: 400, margin: '0 auto 24px' }}>
                {isReview
                    ? 'Your documents are being reviewed by our team. You will be notified when approved and can start managing orders.'
                    : 'Please submit your business documents to gain access to orders and dumpster management.'}
            </div>
            {!isReview && <Btn onClick={onGoToDocs}>Go to Documents →</Btn>}
        </Card>
    );
}

// ── Documents View ────────────────────────────────────────────────────────────

function DocumentsView({ docStatus, onUpdated }: {
    docStatus: VendorDocumentStatusResponse | null;
    onUpdated: (ds: VendorDocumentStatusResponse) => void;
}) {
    const [uploading, setUploading] = useState<string | null>(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const fileInputRef = useRef<HTMLInputElement>(null);
    const pendingDocType = useRef<string>('');

    const handleFileSelect = (docType: string) => {
        pendingDocType.current = docType;
        fileInputRef.current?.click();
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const docType = pendingDocType.current;

        const reader = new FileReader();
        reader.onload = async () => {
            const base64 = reader.result as string;
            setUploading(docType);
            setError(''); setSuccess('');
            try {
                const result = await api.submitVendorDocument(docType, base64);
                onUpdated(result);
                setSuccess(`${DocTypeFriendly(docType)} submitted for review!`);
            } catch (e: any) { setError(e.message); }
            finally { setUploading(null); }
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const docStatusStyle = (s: string) => {
        switch (s) {
            case 'Approved': return { bg: C.greenBg, color: C.green, icon: '✓' };
            case 'Submitted': return { bg: '#EEF2FF', color: '#3730A3', icon: '⏳' };
            case 'Rejected': return { bg: '#FDECEA', color: '#9B1C1C', icon: '✗' };
            default: return { bg: C.bg, color: C.textMuted, icon: '○' };
        }
    };

    const docs = [
        { type: 'BusinessLicense', label: 'Business License', icon: '🏢', status: docStatus?.businessLicenseStatus ?? 'Pending', photoUrl: docStatus?.businessLicensePhotoUrl, rejectionReason: docStatus?.businessLicenseRejectionReason },
        { type: 'LiabilityInsurance', label: 'Liability Insurance', icon: '🛡️', status: docStatus?.liabilityInsuranceStatus ?? 'Pending', photoUrl: docStatus?.liabilityInsurancePhotoUrl, rejectionReason: docStatus?.liabilityInsuranceRejectionReason },
        { type: 'EquipmentDoc', label: 'Equipment / Vehicle Proof', icon: '🚚', status: docStatus?.equipmentDocStatus ?? 'Pending', photoUrl: docStatus?.equipmentDocPhotoUrl, rejectionReason: docStatus?.equipmentDocRejectionReason },
    ];

    const onboardStyle = docStatus?.onboardingStatus === 'Approved' ? { bg: C.greenBg, color: C.green }
        : docStatus?.onboardingStatus === 'UnderReview' ? { bg: '#EEF2FF', color: '#3730A3' }
        : { bg: C.bg, color: C.textMuted };

    return (
        <div>
            <input ref={fileInputRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={handleFileChange} />

            {error && <div style={{ background: '#FDECEA', border: '1px solid #F87171', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 13, color: '#9B1C1C' }}>⚠ {error}</div>}
            {success && <div style={{ background: C.greenBg, border: `1px solid ${C.greenMid}`, borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 13, color: C.green }}>✓ {success}</div>}

            {/* Onboarding status */}
            <div style={{ ...onboardStyle, borderRadius: 10, padding: '14px 18px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 20 }}>
                    {docStatus?.onboardingStatus === 'Approved' ? '✅' : docStatus?.onboardingStatus === 'UnderReview' ? '⏳' : '📋'}
                </span>
                <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>
                        {docStatus?.onboardingStatus === 'Approved' ? 'Onboarding Complete'
                            : docStatus?.onboardingStatus === 'UnderReview' ? 'Documents Under Review'
                            : 'Onboarding Incomplete'}
                    </div>
                    <div style={{ fontSize: 13, opacity: 0.8 }}>
                        {docStatus?.onboardingStatus === 'Approved'
                            ? 'Your account is fully verified. You can manage all orders and dumpsters.'
                            : docStatus?.onboardingStatus === 'UnderReview'
                            ? 'Our team is reviewing your documents. This usually takes 1-2 business days.'
                            : 'Upload all three required documents to complete your onboarding.'}
                    </div>
                </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {docs.map(doc => {
                    const st = docStatusStyle(doc.status);
                    const isUploading = uploading === doc.type;
                    return (
                        <Card key={doc.type} style={{ borderLeft: `4px solid ${st.color}` }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                                        <span style={{ fontSize: 20 }}>{doc.icon}</span>
                                        <span style={{ fontWeight: 700, fontSize: 15 }}>{doc.label}</span>
                                        <span style={{ background: st.bg, color: st.color, borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>
                                            {st.icon} {doc.status}
                                        </span>
                                    </div>
                                    {doc.status === 'Pending' && (
                                        <div style={{ fontSize: 13, color: C.textSub }}>
                                            Upload a clear photo or scan of your {doc.label}.
                                        </div>
                                    )}
                                    {doc.status === 'Rejected' && doc.rejectionReason && (
                                        <div style={{ fontSize: 13, color: '#9B1C1C', background: '#FDECEA', borderRadius: 6, padding: '8px 12px', marginTop: 4 }}>
                                            ✗ Rejection reason: {doc.rejectionReason}
                                        </div>
                                    )}
                                    {doc.photoUrl && (
                                        <img src={doc.photoUrl} alt={doc.label} style={{ maxHeight: 120, borderRadius: 8, border: `1px solid ${C.border}`, marginTop: 8 }} />
                                    )}
                                </div>
                                <div>
                                    {(doc.status === 'Pending' || doc.status === 'Rejected') && (
                                        <Btn size="sm" onClick={() => handleFileSelect(doc.type)} disabled={isUploading}>
                                            {isUploading ? 'Uploading...' : doc.status === 'Rejected' ? '↩ Resubmit' : '📎 Upload'}
                                        </Btn>
                                    )}
                                    {doc.status === 'Submitted' && (
                                        <Btn size="sm" variant="outline" onClick={() => handleFileSelect(doc.type)} disabled={isUploading}>
                                            {isUploading ? 'Uploading...' : '↩ Replace'}
                                        </Btn>
                                    )}
                                    {doc.status === 'Approved' && (
                                        <div style={{ fontSize: 12, color: C.green, fontWeight: 600 }}>✓ Verified</div>
                                    )}
                                </div>
                            </div>
                        </Card>
                    );
                })}
            </div>
        </div>
    );
}

function DocTypeFriendly(t: string) {
    return t === 'BusinessLicense' ? 'Business License'
        : t === 'LiabilityInsurance' ? 'Liability Insurance'
        : 'Equipment/Vehicle proof';
}

// ── Orders View ───────────────────────────────────────────────────────────────

function OrdersView({ orders, loading, onRefresh, statusColor }: {
    orders: any[]; loading: boolean; onRefresh: () => void; statusColor: (s: string) => string;
}) {
    const [filter, setFilter] = useState('Active');
    const [actionLoading, setActionLoading] = useState<string | null>(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [confirmDeclineId, setConfirmDeclineId] = useState<string | null>(null);

    const filters = ['Active', 'Pending', 'Delivered', 'Completed', 'All'];

    const filtered = orders.filter(o => {
        if (filter === 'All') return true;
        if (filter === 'Active') return !['Completed', 'Cancelled'].includes(o.status);
        return o.status === filter || (filter === 'Pending' && o.status === 'Pending');
    });

    const doAction = async (fn: () => Promise<any>, id: string, successMsg?: string) => {
        setActionLoading(id);
        setError(''); setSuccess('');
        try {
            await fn();
            await onRefresh();
            if (successMsg) { setSuccess(successMsg); setTimeout(() => setSuccess(''), 5000); }
        }
        catch (e: any) { setError(e.message || 'Something went wrong. Please try again.'); }
        finally { setActionLoading(null); }
    };

    const handleDecline = async (orderId: string) => {
        setConfirmDeclineId(null);
        await doAction(() => api.vendorDeclineOrder(orderId), orderId, 'Order declined. The customer has been notified.');
    };

    if (loading) return <Spinner />;

    return (
        <div>
            <Alert message={error} />
            {success && (
                <div style={{ background: '#D1FAE5', border: `1px solid ${C.greenMid}`, borderRadius: 8, padding: '10px 16px', marginBottom: 16, color: C.green, fontWeight: 600, fontSize: 14 }}>
                    ✓ {success}
                </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Orders</h2>
                <Btn size="sm" variant="outline" onClick={onRefresh}>↻ Refresh</Btn>
            </div>

            {/* Filter pills */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
                {filters.map(f => (
                    <button key={f} onClick={() => setFilter(f)}
                        style={{ padding: '6px 14px', borderRadius: 20, border: `1px solid ${filter === f ? C.greenMid : C.border}`, background: filter === f ? C.greenBg : '#fff', color: filter === f ? C.green : C.textSub, fontSize: 12, fontWeight: filter === f ? 700 : 400, cursor: 'pointer' }}>
                        {f} {f === 'All' ? `(${orders.length})` : f === 'Active' ? `(${orders.filter(o => !['Completed', 'Cancelled'].includes(o.status)).length})` : `(${orders.filter(o => o.status === f).length})`}
                    </button>
                ))}
            </div>

            {filtered.length === 0 ? (
                <Card style={{ textAlign: 'center', padding: 48 }}>
                    <div style={{ fontSize: 48, marginBottom: 12 }}>🚛</div>
                    <div style={{ fontWeight: 600, fontSize: 16 }}>No orders</div>
                    <div style={{ color: C.textSub, marginTop: 6 }}>New orders will appear here when customers book</div>
                </Card>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {filtered.map(order => (
                        <Card key={order.id} style={{ borderLeft: `4px solid ${statusColor(order.status)}` }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                                        <span style={{ fontWeight: 700, fontSize: 16 }}>🗑️ {order.dumpsterYards}yd Dumpster</span>
                                        <Badge label={order.status} />
                                    </div>
                                    <div style={{ fontSize: 13, color: C.textSub, marginBottom: 4 }}>👤 {order.customerName}</div>
                                    <div style={{ fontSize: 13, color: C.textSub, marginBottom: 4 }}>📍 {order.deliveryAddress}</div>
                                    <div style={{ fontSize: 13, color: C.textSub, marginBottom: 4 }}>
                                        📅 Delivery: {new Date(order.requestedDeliveryDate).toLocaleDateString()} · {order.rentalDays} day rental
                                    </div>
                                    {order.loadingRequested && (
                                        <div style={{ fontSize: 13, color: '#6366F1', marginBottom: 4 }}>
                                            💪 {order.loadingRunnersRequested} loading runner{order.loadingRunnersRequested > 1 ? 's' : ''} requested
                                        </div>
                                    )}
                                    {order.pickupRequestedAt && (
                                        <div style={{ fontSize: 13, color: '#854F0B', fontWeight: 600 }}>
                                            ⚠️ Pickup requested {new Date(order.pickupRequestedAt).toLocaleDateString()}
                                        </div>
                                    )}
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontWeight: 800, fontSize: 22, color: C.greenMid }}>${order.totalCost?.toFixed(2)}</div>
                                    <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>
                                        ${order.deliveryPrice?.toFixed(2)} delivery + ${order.totalRentalCost?.toFixed(2)} rental
                                    </div>
                                    {order.overageCost > 0 && (
                                        <div style={{ fontSize: 12, color: '#854F0B', marginTop: 2 }}>
                                            +${order.overageCost?.toFixed(2)} overage
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Action buttons */}
                            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                {order.status === 'Pending' && (
                                    <>
                                        <Btn onClick={() => doAction(() => api.vendorAcceptOrder(order.id), order.id, 'Order accepted! The customer has been notified.')}
                                            disabled={actionLoading === order.id}>
                                            ✓ Accept order
                                        </Btn>
                                        <Btn variant="outline" onClick={() => setConfirmDeclineId(order.id)}
                                            disabled={actionLoading === order.id}>
                                            ✗ Decline
                                        </Btn>
                                        {confirmDeclineId === order.id && (
                                            <div style={{ width: '100%', background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 8, padding: '12px 16px', marginTop: 4 }}>
                                                <div style={{ fontWeight: 600, fontSize: 14, color: '#991B1B', marginBottom: 8 }}>
                                                    Decline this order?
                                                </div>
                                                <div style={{ fontSize: 13, color: '#7F1D1D', marginBottom: 12 }}>
                                                    The customer will be notified and the payment authorization hold will be released. This cannot be undone.
                                                </div>
                                                <div style={{ display: 'flex', gap: 8 }}>
                                                    <Btn onClick={() => handleDecline(order.id)} disabled={actionLoading === order.id}>
                                                        {actionLoading === order.id ? 'Declining…' : '✗ Yes, decline order'}
                                                    </Btn>
                                                    <Btn variant="outline" onClick={() => setConfirmDeclineId(null)} disabled={actionLoading === order.id}>
                                                        Keep order
                                                    </Btn>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                                {order.status === 'VendorAccepted' && (
                                    <Btn onClick={() => doAction(() => api.vendorMarkDelivered(order.id), order.id)}
                                        disabled={actionLoading === order.id}>
                                        🚛 Mark as delivered
                                    </Btn>
                                )}
                                {order.status === 'PickupRequested' && (
                                    <Btn onClick={() => doAction(() => api.vendorMarkPickupComplete(order.id), order.id)}
                                        disabled={actionLoading === order.id}>
                                        ✅ Mark pickup complete
                                    </Btn>
                                )}
                                {order.status === 'Completed' && (
                                    <div style={{ fontSize: 13, color: C.green, fontWeight: 600, padding: '8px 0' }}>
                                        ✅ Order completed · Payment captured
                                    </div>
                                )}
                            </div>

                            {/* Loading runners status */}
                            {order.loadingJobs?.filter((j: any) => j.contractorName).length > 0 && (
                                <div style={{ marginTop: 16, padding: 12, background: C.bg, borderRadius: 8 }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: C.textSub, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>Loading Runners</div>
                                    {order.loadingJobs.filter((j: any) => j.contractorName).map((job: any) => (
                                        <div key={job.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: `1px solid ${C.borderLight}` }}>
                                            <div>
                                                <span style={{ fontWeight: 500, fontSize: 13 }}>{job.contractorName}</span>
                                                {job.hoursWorked && <span style={{ fontSize: 12, color: C.textSub, marginLeft: 8 }}>{job.hoursWorked.toFixed(2)}h</span>}
                                            </div>
                                            <Badge label={job.status} />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── Sizes View ────────────────────────────────────────────────────────────────

function SizesView() {
    const [vendor, setVendor] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showAdd, setShowAdd] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ yards: '10', description: '', deliveryPrice: '', dailyRate: '', maxRentalDays: '14' });

    useEffect(() => {
        api.getVendorProfile().then(setVendor).catch((e: any) => setError(e.message)).finally(() => setLoading(false));
    }, []);

    const addSize = async () => {
        setSaving(true); setError(''); setSuccess('');
        try {
            await api.addDumpsterSize(vendor.id, {
                yards: parseInt(form.yards),
                description: form.description,
                deliveryPrice: parseFloat(form.deliveryPrice),
                dailyRate: parseFloat(form.dailyRate),
                maxRentalDays: parseInt(form.maxRentalDays),
            });
            const updated = await api.getVendorProfile();
            setVendor(updated);
            setSuccess('Size added!');
            setShowAdd(false);
            setForm({ yards: '10', description: '', deliveryPrice: '', dailyRate: '', maxRentalDays: '14' });
        } catch (e: any) { setError(e.message); }
        finally { setSaving(false); }
    };

    if (loading) return <Spinner />;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>My Dumpster Sizes</h2>
                <Btn onClick={() => setShowAdd(!showAdd)}>+ Add Size</Btn>
            </div>
            <Alert message={error} />
            {success && <div style={{ background: C.greenBg, color: C.green, padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontWeight: 600 }}>✓ {success}</div>}

            {showAdd && (
                <Card style={{ marginBottom: 24, borderLeft: `4px solid ${C.greenMid}` }}>
                    <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16 }}>Add New Size</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
                        <div>
                            <label style={{ fontSize: 12, fontWeight: 500, color: C.textSub, display: 'block', marginBottom: 6 }}>Size (yards)</label>
                            <select value={form.yards} onChange={e => setForm(f => ({ ...f, yards: e.target.value }))}
                                style={{ width: '100%', padding: '10px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 14 }}>
                                {[10, 15, 20, 30, 40].map(y => <option key={y} value={y}>{y}yd</option>)}
                            </select>
                        </div>
                        <Input label="Delivery price ($)" value={form.deliveryPrice} onChange={v => setForm(f => ({ ...f, deliveryPrice: v }))} type="number" />
                        <Input label="Daily rate ($)" value={form.dailyRate} onChange={v => setForm(f => ({ ...f, dailyRate: v }))} type="number" />
                    </div>
                    <Input label="Description" value={form.description} onChange={v => setForm(f => ({ ...f, description: v }))} placeholder="e.g. Great for small home cleanouts" />
                    <Input label="Max rental days" value={form.maxRentalDays} onChange={v => setForm(f => ({ ...f, maxRentalDays: v }))} type="number" />
                    <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                        <Btn variant="outline" onClick={() => setShowAdd(false)}>Cancel</Btn>
                        <Btn onClick={addSize} disabled={saving}>{saving ? 'Saving...' : 'Add size'}</Btn>
                    </div>
                </Card>
            )}

            {vendor?.sizes?.length === 0 ? (
                <Card style={{ textAlign: 'center', padding: 48 }}>
                    <div style={{ fontSize: 48, marginBottom: 12 }}>🗑️</div>
                    <div style={{ fontWeight: 600 }}>No sizes yet</div>
                    <div style={{ color: C.textSub, marginTop: 6 }}>Add sizes so customers can book your dumpsters</div>
                </Card>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
                    {vendor?.sizes?.map((size: any) => (
                        <Card key={size.id} style={{ borderTop: `3px solid ${size.isAvailable ? C.greenMid : C.border}` }}>
                            <div style={{ fontWeight: 800, fontSize: 32, color: C.green, marginBottom: 4 }}>{size.yards}yd</div>
                            <div style={{ fontSize: 13, color: C.textSub, marginBottom: 12 }}>{size.description}</div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                                <span style={{ color: C.textSub }}>Delivery</span>
                                <span style={{ fontWeight: 600 }}>${size.deliveryPrice.toFixed(2)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                                <span style={{ color: C.textSub }}>Daily rate</span>
                                <span style={{ fontWeight: 600 }}>${size.dailyRate.toFixed(2)}/day</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 12 }}>
                                <span style={{ color: C.textSub }}>Max rental</span>
                                <span style={{ fontWeight: 600 }}>{size.maxRentalDays} days</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{ width: 8, height: 8, borderRadius: '50%', background: size.isAvailable ? C.greenMid : C.textMuted }} />
                                <span style={{ fontSize: 12, color: size.isAvailable ? C.green : C.textMuted, fontWeight: 600 }}>
                                    {size.isAvailable ? 'Available' : 'Unavailable'}
                                </span>
                            </div>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── Earnings View ─────────────────────────────────────────────────────────────

function EarningsView({ orders }: { orders: any[] }) {
    const [connectStatus, setConnectStatus] = useState<VendorConnectStatusResponse | null>(null);
    const [payoutHistory, setPayoutHistory] = useState<VendorPayoutHistoryItem[]>([]);
    const [connectLoading, setConnectLoading] = useState(true);
    const [onboardingLoading, setOnboardingLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [connectError, setConnectError] = useState('');

    useEffect(() => {
        Promise.all([
            api.getVendorConnectStatus().catch(() => null),
            api.getVendorPayoutHistory().catch(() => []),
        ]).then(([status, history]) => {
            if (status) setConnectStatus(status);
            setPayoutHistory(history as VendorPayoutHistoryItem[]);
        }).finally(() => setConnectLoading(false));
    }, []);

    const handleStartOnboarding = async () => {
        setOnboardingLoading(true);
        setConnectError('');
        try {
            const returnUrl = `${window.location.origin}/vendor?connect=return`;
            const refreshUrl = `${window.location.origin}/vendor?connect=refresh`;
            const { url } = await api.getVendorOnboardingUrl(returnUrl, refreshUrl);
            window.location.href = url;
        } catch (e: any) {
            setConnectError(e.message);
            setOnboardingLoading(false);
        }
    };

    const handleRefreshStatus = async () => {
        setRefreshing(true);
        setConnectError('');
        try {
            const status = await api.refreshVendorConnect();
            setConnectStatus(status);
        } catch (e: any) {
            setConnectError(e.message);
        } finally { setRefreshing(false); }
    };

    const completed = orders.filter(o => o.status === 'Completed');
    const pending = orders.filter(o => !['Completed', 'Cancelled'].includes(o.status));

    const payoutStatusStyle = (status: string) => {
        if (status === 'Transferred') return { color: C.green, bg: C.greenBg };
        if (status === 'Failed') return { color: '#9B1C1C', bg: '#FDECEA' };
        if (status === 'Skipped') return { color: '#854F0B', bg: '#FFF3E0' };
        return { color: C.textMuted, bg: C.bg };
    };

    return (
        <div>
            <h2 style={{ margin: '0 0 20px', fontSize: 20, fontWeight: 700 }}>Earnings & Payouts</h2>

            {/* Stripe Connect status */}
            {connectLoading ? <Spinner /> : (
                <Card style={{ marginBottom: 24, padding: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                        <div>
                            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>
                                {connectStatus?.onboardingComplete ? '✅ Stripe payouts connected' : '💳 Connect Stripe to receive payouts'}
                            </div>
                            <div style={{ fontSize: 13, color: C.textSub }}>
                                {connectStatus?.onboardingComplete
                                    ? `Account ${connectStatus.accountId} — payouts are transferred automatically when orders complete.`
                                    : 'Set up a Stripe Express account to receive direct payouts for completed orders.'}
                            </div>
                            {connectError && <div style={{ marginTop: 8, fontSize: 13, color: '#9B1C1C' }}>{connectError}</div>}
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                            {connectStatus?.onboardingComplete ? (
                                <Btn onClick={handleRefreshStatus} disabled={refreshing} style={{ fontSize: 13 }}>
                                    {refreshing ? 'Refreshing…' : 'Refresh status'}
                                </Btn>
                            ) : (
                                <>
                                    <Btn onClick={handleStartOnboarding} disabled={onboardingLoading} style={{ fontSize: 13 }}>
                                        {onboardingLoading ? 'Redirecting…' : connectStatus?.hasAccount ? 'Continue setup' : 'Set up payouts'}
                                    </Btn>
                                    {connectStatus?.hasAccount && (
                                        <Btn onClick={handleRefreshStatus} disabled={refreshing} style={{ fontSize: 13, background: 'none', color: C.textSub, border: `1px solid ${C.border}` }}>
                                            {refreshing ? '…' : 'Check status'}
                                        </Btn>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </Card>
            )}

            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
                {[
                    { label: 'Total paid out', value: connectStatus ? `$${connectStatus.totalEarned.toFixed(2)}` : '—', color: C.greenMid },
                    { label: 'Pending payout', value: connectStatus ? `$${connectStatus.pendingPayout.toFixed(2)}` : `$${pending.reduce((s: number, o: any) => s + (o.totalCost || 0), 0).toFixed(2)}`, color: '#854F0B' },
                    { label: 'Orders completed', value: String(completed.length), color: C.text },
                ].map(s => (
                    <Card key={s.label} style={{ textAlign: 'center', padding: 20 }}>
                        <div style={{ fontSize: 28, fontWeight: 800, color: s.color }}>{s.value}</div>
                        <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>{s.label}</div>
                    </Card>
                ))}
            </div>

            {/* Payout history (from Stripe Connect data) */}
            {payoutHistory.length > 0 && (
                <Card style={{ padding: 0, overflow: 'hidden', marginBottom: 24 }}>
                    <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`, fontWeight: 700, fontSize: 14 }}>
                        Payout History
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead>
                                <tr style={{ background: C.bg }}>
                                    {['Address', 'Date', 'Order Revenue', 'Your Payout', 'Status'].map(h => (
                                        <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: C.textSub, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {payoutHistory.map((p, i) => {
                                    const st = payoutStatusStyle(p.payoutStatus);
                                    return (
                                        <tr key={p.orderId} style={{ background: i % 2 === 0 ? '#fff' : C.bg }}>
                                            <td style={{ padding: '10px 16px', color: C.textSub, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.deliveryAddress}</td>
                                            <td style={{ padding: '10px 16px', whiteSpace: 'nowrap' }}>{new Date(p.completedAt).toLocaleDateString()}</td>
                                            <td style={{ padding: '10px 16px' }}>${p.orderRevenue.toFixed(2)}</td>
                                            <td style={{ padding: '10px 16px', fontWeight: 700, color: C.green }}>${p.vendorPayout.toFixed(2)}</td>
                                            <td style={{ padding: '10px 16px' }}>
                                                <span style={{ ...st, padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{p.payoutStatus}</span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}

            {/* Completed orders breakdown */}
            <Card style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`, fontWeight: 700, fontSize: 14 }}>
                    Completed Orders
                </div>
                {completed.length === 0 ? (
                    <div style={{ padding: 32, textAlign: 'center', color: C.textMuted }}>No completed orders yet</div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead>
                                <tr style={{ background: C.bg }}>
                                    {['Customer', 'Size', 'Address', 'Days', 'Delivery', 'Rental', 'Overage', 'Order Total', 'Your Payout', 'Date'].map(h => (
                                        <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: C.textSub, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {completed.map((o, i) => (
                                    <tr key={o.id} style={{ background: i % 2 === 0 ? '#fff' : C.bg }}>
                                        <td style={{ padding: '10px 16px', fontWeight: 500 }}>{o.customerName}</td>
                                        <td style={{ padding: '10px 16px' }}>{o.dumpsterYards}yd</td>
                                        <td style={{ padding: '10px 16px', color: C.textSub, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.deliveryAddress}</td>
                                        <td style={{ padding: '10px 16px' }}>{o.rentalDays}d</td>
                                        <td style={{ padding: '10px 16px' }}>${o.deliveryPrice?.toFixed(2)}</td>
                                        <td style={{ padding: '10px 16px' }}>${o.totalRentalCost?.toFixed(2)}</td>
                                        <td style={{ padding: '10px 16px', color: o.overageCost > 0 ? '#854F0B' : C.textMuted }}>
                                            {o.overageCost > 0 ? `$${o.overageCost.toFixed(2)}` : '—'}
                                        </td>
                                        <td style={{ padding: '10px 16px' }}>${o.totalCost?.toFixed(2)}</td>
                                        <td style={{ padding: '10px 16px', fontWeight: 700, color: o.vendorPayout != null ? C.green : C.textMuted }}>
                                            {o.vendorPayout != null ? `$${o.vendorPayout.toFixed(2)}` : '—'}
                                        </td>
                                        <td style={{ padding: '10px 16px', color: C.textMuted, whiteSpace: 'nowrap' }}>{new Date(o.createdAt).toLocaleDateString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>
        </div>
    );
}
